import {
  Box,
  TextField,
  Fade,
  Modal,
  Typography,
  Grid,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  Button,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWR from 'swr';
import * as Yup from 'yup';

import { gradientBackground } from '../../../../assets/styles/button.style';
import CheckedIcon from '../../../../assets/svg/checkedIcon.svg';
import CheckIcon from '../../../../assets/svg/checkIcon.svg';
import DeleteIcon from '../../../../assets/svg/deleteIcon.svg';
import EditIcon from '../../../../assets/svg/editIcon.svg';
import { axiosInstance } from '../../../../assets/utils/axiosInstance';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import { CustomError } from '../../../../interfaces/ErrorType';
import HeaderTextComponent from '../../../common/headerTextComponent';

interface IPAddressesProps {
  id: number;
  name: string;
  ip_address: string;
  is_active: boolean;
}

interface IPAddressFormModalProps {
  open: boolean;
  handleClose: () => void;
  isEditing: boolean;
  data: IPAddressesProps;
  ipMutate: () => void;
}

interface RowActionsProps {
  data: IPAddressesProps;
  open: boolean;
  handleEdit: (data: IPAddressesProps) => void;
  handleActive: (data: IPAddressesProps) => void;
  handleDelete: (data: IPAddressesProps) => void;
}

const fetcher = (url: string) =>
  axiosInstance()
    .get(url)
    .then((res) => res.data);

const IPAddressFormModal = ({
  open,
  handleClose,
  isEditing,
  data,
  ipMutate,
}: IPAddressFormModalProps) => {
  const {
    data: tenantData,
    isLoading: tenantIsLoading,
    error: tenantError,
  } = useSWR('tenant', fetcher);
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();

  const ipForm = useFormik({
    initialValues: !isEditing
      ? {
        id: 0,
        ipName: '',
        ipAddress: '',
        submit: null,
      }
      : {
        id: data.id,
        ipName: data.name,
        ipAddress: data.ip_address,
        isActive: data.is_active,
        submit: null,
      },
    enableReinitialize: true,
    validationSchema: Yup.object({
      ipName: Yup.string()
        .max(255)
        .required(
          intl.formatMessage({ defaultMessage: 'IP name is required' }),
        ),
      ipAddress: Yup.string()
        .max(255)
        .required(
          intl.formatMessage({ defaultMessage: 'IP address is required' }),
        )
        .matches(/(^(\d{1,3}\.){3}(\d{1,3})$)/, {
          message: intl.formatMessage({ defaultMessage: 'Invalid IP address' }),
          excludeEmptyString: true,
        })
        .test(
          'ip',
          intl.formatMessage({ defaultMessage: 'Invalid IP address' }),
          (value) => {
            return value === undefined || value.trim() === ''
              ? true
              : value.split('.').find((i) => parseInt(i, 10) > 255) ===
                  undefined;
          },
        ),
    }),
    onSubmit: async (values, helpers) => {
      try {
        if (!isEditing) {
          await axiosInstance().post('allowed-ip-address', {
            name: values.ipName,
            ip_address: values.ipAddress,
            is_active: true,
            tenant: tenantData.id,
          });
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'IP added successfully' }),
            'success',
          );
        } else {
          await axiosInstance().patch(`allowed-ip-address/${values.id}`, {
            name: values.ipName,
            ip_address: values.ipAddress,
            is_active: values.isActive,
            tenant: tenantData.id,
          });
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'IP edited successfully' }),
            'success',
          );
        }
        ipMutate();
        helpers.resetForm();
        handleClose();
      } catch (error) {
        helpers.setStatus({ success: false });

        const customError = error as CustomError;
        const errorMessage =
          customError.message ||
          // customError.request?.responseText ||
          intl.formatMessage({
            defaultMessage: 'Something went wrong, please try again.',
          });
        helpers.setErrors({ submit: errorMessage });
        helpers.setSubmitting(false);
        createToastNotification(errorMessage, 'error');
      }
    },
  });

  if (tenantIsLoading) return null;

  if (tenantError)
    return (
      <Typography>
        <FormattedMessage defaultMessage='Something went wrong while fetching tenant data' />
      </Typography>
    );

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          handleClose();
          ipForm.resetForm();
        }}
        closeAfterTransition
      >
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute' as const,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '480px',
              bgcolor: 'rgba(255, 255, 255, 1)',
              borderRadius: '6px',
              boxShadow: 24,
              p: '32px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <form id='ipForm' onSubmit={ipForm.handleSubmit}>
              {/* IP Name */}
              <Typography
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                <FormattedMessage defaultMessage='IP Name' />
              </Typography>
              <TextField
                fullWidth
                hiddenLabel
                name='ipName'
                id='ipName'
                placeholder={intl.formatMessage({
                  defaultMessage: 'Enter IP Name',
                })}
                type='text'
                autoComplete='off'
                variant='filled'
                onBlur={ipForm.handleBlur}
                onChange={ipForm.handleChange}
                value={ipForm.values.ipName}
                margin='normal'
                sx={{
                  height: '46px',
                  marginTop: '12 px',
                  borderRadius: '6px',
                  '& .MuiInputBase-input': {
                    paddingLeft: '20px', // Add left padding to the input text
                  },
                  '& .MuiInputBase-input::placeholder': {
                    paddingLeft: '20px', // Add left padding to the placeholder text
                  },
                  '& .MuiFilledInput-root:after': {
                    border: 'none',
                  },
                  '& .MuiFilledInput-root:before': {
                    borderBottom: 'none',
                    height: '46px',
                  },
                  '& .MuiFilledInput-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  },
                  '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                    {
                      borderBottom: 'none',
                    },
                  ...(ipForm.values.ipName && {
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#FFFFFF',
                    },
                    '& .MuiFormLabel-root': {
                      fontFamily: 'Schibsted Grotesk',
                      fontSize: '14px',
                      fontWeight: 400,
                      textAlign: 'left',
                      color: '#000000',
                    },
                  }),
                }}
                InputProps={{
                  style: {
                    height: '46px',
                    overflow: 'hidden',
                    paddingLeft: '0.5rem',
                    paddingRight: '0.5rem',
                    border: ipForm.values.ipName
                      ? '1px solid rgba(0, 0, 0, 0.5)'
                      : '',
                    borderRadius: '6px',
                  },
                }}
              />
              {ipForm.touched.ipName && Boolean(ipForm.errors.ipName) && (
                <Typography
                  sx={{
                    color: 'red',
                    fontSize: '12px',
                    ml: '5px',
                  }}
                >
                  {ipForm.errors.ipName}
                </Typography>
              )}

              {/* IP Address */}
              <Typography
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                <FormattedMessage defaultMessage='IP Address' />
              </Typography>
              <TextField
                fullWidth
                hiddenLabel
                name='ipAddress'
                id='ipAddress'
                placeholder='e.g 192.158.1.38'
                type='text'
                autoComplete='off'
                variant='filled'
                onBlur={ipForm.handleBlur}
                onChange={ipForm.handleChange}
                value={ipForm.values.ipAddress}
                margin='normal'
                sx={{
                  height: '46px',
                  marginTop: '12 px',
                  borderRadius: '6px',
                  '& .MuiInputBase-input': {
                    paddingLeft: '20px', // Add left padding to the input text
                  },
                  '& .MuiInputBase-input::placeholder': {
                    paddingLeft: '20px', // Add left padding to the placeholder text
                  },
                  '& .MuiFilledInput-root:after': {
                    border: 'none',
                  },
                  '& .MuiFilledInput-root:before': {
                    borderBottom: 'none',
                  },
                  '& .MuiFilledInput-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  },
                  '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                    {
                      borderBottom: 'none',
                    },
                  ...(ipForm.values.ipAddress && {
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#FFFFFF',
                    },
                    '& .MuiFormLabel-root': {
                      fontFamily: 'Schibsted Grotesk',
                      fontSize: '14px',
                      fontWeight: 400,
                      textAlign: 'left',
                      color: '#000000',
                    },
                  }),
                }}
                InputProps={{
                  style: {
                    height: '3.5rem',
                    overflow: 'hidden',
                    paddingLeft: '0.5rem',
                    paddingRight: '0.5rem',
                    border: ipForm.values.ipAddress
                      ? '1px solid rgba(0, 0, 0, 0.5)'
                      : '',
                    borderRadius: '6px',
                  },
                }}
              />
              {ipForm.touched.ipAddress && Boolean(ipForm.errors.ipAddress) && (
                <Typography
                  sx={{
                    color: 'red',
                    fontSize: '12px',
                    ml: '5px',
                  }}
                >
                  {ipForm.errors.ipAddress}
                </Typography>
              )}

              <Grid container mt={4}>
                <Grid item xs={6}>
                  <Button
                    variant='text'
                    sx={{
                      display: 'grid',
                      justifyContent: 'center',
                      alignContent: 'center',
                      width: '100%',
                      color: '#181818',
                      height: '3rem',
                    }}
                    onClick={() => {
                      handleClose();
                      ipForm.resetForm();
                    }}
                  >
                    <FormattedMessage defaultMessage='Cancel' />
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    sx={{
                      display: 'grid',
                      justifyContent: 'center',
                      width: '100%',
                      ...gradientBackground,
                    }}
                    type='submit'
                    form='ipForm'
                  >
                    <FormattedMessage defaultMessage='Save' />
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

const RowActions = ({
  data,
  open,
  handleEdit,
  handleActive,
  handleDelete,
}: RowActionsProps) => {
  const editIP = () => {
    handleEdit(data);
  };

  const setIP = () => {
    const editData = { ...data };
    editData.is_active = !data.is_active;
    handleActive(editData);
  };

  const deleteIP = () => {
    handleDelete(data);
  };

  return (
    <Stack direction='row' justifyContent='flex-end' alignItems='flex-end'>
      {open && (
        <IconButton aria-label='edit' onClick={editIP}>
          <img src={EditIcon} alt='Logo' style={{ height: 20 }} />
        </IconButton>
      )}
      {open && (
        <IconButton aria-label='delete' onClick={deleteIP}>
          <img src={DeleteIcon} alt='Logo' style={{ height: 20 }} />
        </IconButton>
      )}
      <Checkbox
        icon={<img src={CheckIcon} alt='Logo' style={{ height: 20 }} />}
        checkedIcon={
          <img src={CheckedIcon} alt='Logo' style={{ height: 20 }} />
        }
        checked={data.is_active}
        onChange={setIP}
      />
    </Stack>
  );
};

const SecuritySettingsIPAddress = () => {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<IPAddressesProps>({
    id: 0,
    name: '',
    ip_address: '',
    is_active: false,
  });
  const [rowHovered, setRowHovered] = useState(false);
  const [rowIndex, setRowIndex] = useState<number>();
  const intl = useIntl();
  const IP_ENABLED_MESSAGE = intl.formatMessage({
    defaultMessage: 'IP enabled successfully',
  });
  const IP_DISABLED_MESSAGE = intl.formatMessage({
    defaultMessage: 'IP disabled successfully',
  });
  const { createToastNotification } = useToastNotificationContext();
  const {
    data: ipData,
    isLoading: ipDataIsLoading,
    error: ipDataError,
    mutate,
  } = useSWR('allowed-ip-address', fetcher, {
    revalidateOnFocus: true,
  });

  const handleClose = () => {
    setIsEditing(false);
    setOpen(false);
  };

  const handleEdit = (data: IPAddressesProps) => {
    setIsEditing(true);
    setEditData(data);
    setOpen(true);
  };

  const handleActive = async (data: IPAddressesProps) => {
    try {
      await axiosInstance().patch(`allowed-ip-address/${data.id}`, {
        is_active: data.is_active,
      });
      mutate();
      createToastNotification(
        `${data.is_active ? IP_ENABLED_MESSAGE : IP_DISABLED_MESSAGE}`,
        'success',
      );
    } catch (error) {
      const customError = error as CustomError;
      const errorMessage =
        customError.message ||
        intl.formatMessage({
          defaultMessage: 'Something went wrong, please try again.',
        });
      createToastNotification(errorMessage, 'error');
    }
  };

  const handleDelete = async (data: IPAddressesProps) => {
    try {
      await axiosInstance().delete(`allowed-ip-address/${data.id}`);
      mutate();
      createToastNotification(
        intl.formatMessage({ defaultMessage: 'IP deleted successfully' }),
        'success',
      );
    } catch (error) {
      const customError = error as CustomError;
      const errorMessage =
        customError.message ||
        intl.formatMessage({
          defaultMessage: 'Something went wrong, please try again.',
        });
      createToastNotification(errorMessage, 'error');
    }
  };

  const handleCellHover = (index: number, hoveredState: boolean) => {
    setRowHovered(hoveredState);
    setRowIndex(index);
  };

  if (ipDataError)
    return (
      <Box pt={4} pl={2} pr={3}>
        <Typography
          sx={{
            color: 'rgba(135, 135, 135, 1)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '16px',
          }}
        >
          <FormattedMessage defaultMessage='Error occurred while fetching' />
        </Typography>
      </Box>
    );

  return (
    <Box pt={4} pl={2} pr={3}>
      <Grid container>
        <Grid item xs={6}>
          <HeaderTextComponent variant='h1'>
            <FormattedMessage defaultMessage='IP Address' />
          </HeaderTextComponent>
        </Grid>
        <Grid item xs={6}>
          <Typography
            sx={{
              color: '#00CECF',
              fontFamily: 'Schibsted Grotesk',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              float: 'right',
              display: 'flex',
              justifyItems: 'flex-end',
              cursor: 'pointer',
            }}
            onClick={() => {
              setOpen(true);
            }}
          >
            <FormattedMessage defaultMessage='Add IP Address' />
          </Typography>
        </Grid>
      </Grid>
      <Box my={2}>
        <Typography
          sx={{
            color: 'rgba(135, 135, 135, 1)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '16px',
            fontWeight: 400,
          }}
        >
          <FormattedMessage defaultMessage='Configure and manage permitted IP Addresses for accessing the portal' />
        </Typography>
      </Box>
      <Box>
        <TableContainer>
          <Table size='small' padding='none'>
            <TableHead>
              <TableRow>
                <TableCell
                  align='left'
                  sx={{ border: 'none', px: '12px', width: '33%' }}
                >
                  <Typography
                    sx={{
                      color: 'rgba(135, 135, 135, 1)',
                      fontFamily: 'Schibsted Grotesk',
                      fontSize: '14px',
                    }}
                  >
                    <FormattedMessage defaultMessage='IP Name' />
                  </Typography>
                </TableCell>
                <TableCell
                  align='left'
                  sx={{ border: 'none', px: '12px', width: '33%' }}
                >
                  <Typography
                    sx={{
                      color: 'rgba(135, 135, 135, 1)',
                      fontFamily: 'Schibsted Grotesk',
                      fontSize: '14px',
                    }}
                  >
                    <FormattedMessage defaultMessage='IP Address' />
                  </Typography>
                </TableCell>
                <TableCell
                  align='right'
                  sx={{ border: 'none', px: '12px', width: '33%' }}
                >
                  <Typography
                    sx={{
                      color: 'rgba(135, 135, 135, 1)',
                      fontFamily: 'Schibsted Grotesk',
                      fontSize: '14px',
                    }}
                  >
                    <FormattedMessage defaultMessage='Set as' />
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableHead>
              {ipDataIsLoading ? (
                <TableRow>
                  <TableCell colSpan={3} align='center' sx={{ pt: 2 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : ipData.results.length != 0 ? (
                ipData.results.map((data: IPAddressesProps, index: number) => (
                  <TableRow
                    hover
                    key={index}
                    onMouseEnter={() => handleCellHover(index, true)}
                    onMouseLeave={() => handleCellHover(index, false)}
                  >
                    <TableCell
                      align='left'
                      sx={{ border: 'none', px: '12px', width: '33%' }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          fontFamily: 'Schibsted Grotesk',
                          fontSize: '16px',
                        }}
                      >
                        {data.name}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align='left'
                      sx={{ border: 'none', px: '12px', width: '33%' }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Schibsted Grotesk',
                          fontSize: '16px',
                        }}
                      >
                        {data.ip_address}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{ border: 'none', px: '12px', width: '33%' }}
                    >
                      <RowActions
                        data={data}
                        open={rowIndex === index && rowHovered}
                        handleEdit={handleEdit}
                        handleActive={handleActive}
                        handleDelete={handleDelete}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    align='left'
                    colSpan={5}
                    sx={{ border: 'none', px: '12px', pt: 1 }}
                  >
                    <Typography
                      sx={{
                        color: 'rgba(135, 135, 135, 1)',
                        fontFamily: 'Schibsted Grotesk',
                        fontSize: '16px',
                      }}
                    >
                      <FormattedMessage defaultMessage='No IP Address Available' />
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableHead>
          </Table>
        </TableContainer>
      </Box>
      <IPAddressFormModal
        open={open}
        handleClose={handleClose}
        isEditing={isEditing}
        data={editData}
        ipMutate={mutate}
      />
    </Box>
  );
};
export default SecuritySettingsIPAddress;
